import { memo, useCallback, useEffect, useEffectEvent, useMemo, useRef } from 'react';

import { type NextQuestionResponse } from '@api/domain/qualification/validators';
import { hashKey, type QueryKey } from '@tanstack/react-query';

import StateMessage from '@/components/chat/StateMessage';
import { getLocalStorageInstance } from '@/lib/localStorage';
import { trpc } from '@/lib/trpc';
import useAuthStore from '@/stores/auth';
import { type Step, stepStore } from '@/stores/steps';

const QuestionRequest = () => {
  const now = useRef(new Date().getTime());
  const eduzzId = useAuthStore(state => state.currentUser?.eduzzId);
  const sessionId = useMemo(() => {
    const session = getLocalStorageInstance()?.getItem('qualifier-onboarding-session-id');

    if (!session || !eduzzId) {
      return undefined;
    }

    const [sessionEduzzId, id] = session.split(':');

    if (Number(sessionEduzzId) !== eduzzId) {
      return undefined;
    }

    return Number(id);
  }, [eduzzId]);

  const { data, isLoading, error, refetch } = trpc.qualification.nextQuestion.useQuery(
    {
      sessionId: sessionId || 0
    },
    { enabled: Boolean(sessionId), queryKeyHashFn: (queryKey: QueryKey) => hashKey([queryKey, now.current]) }
  );

  const sendAnswer = trpc.qualification.sendAnswer.useMutation();

  const getStepWithData = useCallback(
    (questionData: NextQuestionResponse) => {
      const step: Step = {
        content: 'chat_answer_request',
        merge: true,
        delay: 500,
        hidePreview: true
      };

      const type = (() => {
        if (questionData.question.questionType === 'multiple_choice') {
          return questionData.question.allowMultiple ? 'multiple' : 'single';
        }

        return 'text';
      })();

      const awaitOnAnswer = async (answer: string[]) => {
        if (!sessionId) {
          return;
        }

        if (!questionData.isLastQuestion) {
          stepStore.getState().addSteps({
            steps: [
              {
                content: 'chat_question_request',
                merge: true,
                delay: 500,
                hidePreview: true
              }
            ]
          });
        }

        if (questionData.isLastQuestion) {
          getLocalStorageInstance()?.removeItem('qualifier-onboarding-session-id');
        }

        await sendAnswer.mutateAsync({ sessionId, answer, questionId: questionData.question.id, type });
      };

      if (questionData.question.questionType === 'multiple_choice' && 'options' in questionData) {
        step.data = {
          type,
          options: questionData.options.map(option => ({
            value: option.id.toString(),
            content: {
              description: option.text
            }
          })),
          awaitOnAnswer
        };
        return step;
      }

      step.data = {
        type,
        awaitOnAnswer
      };
      return step;
    },
    [sendAnswer, sessionId]
  );

  const getStep = useEffectEvent(getStepWithData);

  useEffect(() => {
    if (!data) {
      return;
    }

    const step = getStep(data);
    stepStore.getState().addSteps({ steps: [step] });
    stepStore.getState().nextStep();
  }, [data]);

  return (
    <StateMessage
      type='question'
      loading={isLoading && 'Escrevendo...'}
      error={Boolean(error) && 'Não consegui escolher a pergunta correta'}
      onRetry={refetch}
    >
      {data?.question.text}
    </StateMessage>
  );
};

export default memo(QuestionRequest);
